import { useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inputCls } from "@/components/Fields";

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];

export function PhotoUpload({ title, stepText, onDone, testid, askTitle = false, doneMsg = "Photo uploaded (unverified). Save the exercise to keep it." }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(title);
  const [file, setFile] = useState(null);
  const [side, setSide] = useState("right");
  const [alt, setAlt] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const pick = (f) => {
    if (f && !TYPES.includes(f.type)) return toast.error("Choose a JPG, PNG or WebP photo");
    if (f && f.size > 8 * 1024 * 1024) return toast.error("Photo must be 8 MB or smaller");
    setFile(f);
  };
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file); fd.append("title", name || title); fd.append("alt_text", alt || `Photo: ${stepText}`);
    fd.append("laterality", side); fd.append("no_patient_confirmed", ok ? "true" : "false");
    setBusy(true);
    try { const { data } = await api.post("/illustrations/upload", fd); toast.success(doneMsg); onDone(data); setOpen(false); setFile(null); setOk(false); setAlt(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => { setAlt(`Photo: ${stepText}`); setOpen(true); }} className="inline-flex items-center gap-1 rounded border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-800 hover:bg-sky-100"><Camera size={12} />Upload photo</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="photo-upload-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Upload step photo</DialogTitle><DialogDescription className="text-[12px]">{stepText}</DialogDescription></DialogHeader>
          <div className="space-y-3 text-[13px]">
            <input data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={(e) => pick(e.target.files?.[0] || null)} className="block w-full text-[12px]" />
            {askTitle && <label className="block">Title
              <input data-testid="photo-title-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </label>}
            <label className="block">Side shown in the photo
              <select data-testid="photo-side-select" value={side} onChange={(e) => setSide(e.target.value)} className={inputCls}><option value="right">Right</option><option value="left">Left</option><option value="bilateral">Both sides</option></select>
            </label>
            <label className="block">Alt text (required before verification)
              <input data-testid="photo-alt-input" value={alt} onChange={(e) => setAlt(e.target.value)} className={inputCls} />
            </label>
            <label className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
              <input data-testid="photo-no-patient-checkbox" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5" />
              Only a model or staff member appears. No Patient is shown and nobody in the photo can be identified as a Patient.
            </label>
            <p className="text-[11px] text-slate-500">Location and camera details are removed on upload. The photo stays unverified until a Clinical Reviewer checks it.</p>
            <button data-testid="photo-upload-submit" disabled={!file || !ok || busy} onClick={submit} className="inline-flex items-center gap-1.5 rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />}Upload</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
